// @ts-check
import moment from 'moment';
import uid from 'uid';
import api from 'utils/api';
import FlashMessageActions from 'shared/actions/FlashMessageActions';
import { getStore } from 'shared/stash';

const REQUESTING_SCHEDULE_BASE_DATA = 'REQUESTING_SCHEDULE_BASE_DATA';
const RECEIVE_SCHEDULE_BASE_DATA = 'RECEIVE_SCHEDULE_BASE_DATA';
const REQUESTING_APPOINTMENT_SLOTS = 'REQUESTING_APPOINTMENT_SLOTS';
const RECEIVE_APPOINTMENT_SLOTS = 'RECEIVE_APPOINTMENT_SLOTS';
const NAVIGATE_DATE = 'NAVIGATE_DATE';
const UPDATE_VIEW = 'UPDATE_VIEW';
const SELECT_EVENT = 'SELECT_EVENT';
const NEW_SLOT_SELECTION = 'NEW_SLOT_SELECTION';
const HIDE_MODAL = 'HIDE_MODAL';
const CHANGE_SELECTED_PROVIDER_EMPLOYMENT = 'CHANGE_SELECTED_PROVIDER_EMPLOYMENT';
const CHANGE_SELECTED_LOCATION = 'CHANGE_SELECTED_LOCATION';
const CREATE_SLOT_SUCCESS = 'CREATE_SLOT_SUCCESS';
const CREATE_BLOCK_SUCCESS = 'CREATE_BLOCK_SUCCESS';
const UPDATE_BLOCK_SUCCESS = 'UPDATE_BLOCK_SUCCESS';
const UPDATE_SLOT_SUCCESS = 'UPDATE_SLOT_SUCCESS';
const DESTROY_SLOT_SUCCESS = 'DESTROY_SLOT_SUCCESS';
const DESTROY_SLOT_FAILURE = 'DESTROY_SLOT_FAILURE';
const FETCHING_APPOINTMENT = 'FETCHING_APPOINTMENT';
const RECEIVE_APPOINTMENT = 'RECEIVE_APPOINTMENT';

export {
  REQUESTING_SCHEDULE_BASE_DATA,
  RECEIVE_SCHEDULE_BASE_DATA,
  REQUESTING_APPOINTMENT_SLOTS,
  RECEIVE_APPOINTMENT_SLOTS,
  NAVIGATE_DATE,
  UPDATE_VIEW,
  SELECT_EVENT,
  NEW_SLOT_SELECTION,
  HIDE_MODAL,
  CHANGE_SELECTED_PROVIDER_EMPLOYMENT,
  CHANGE_SELECTED_LOCATION,
  CREATE_SLOT_SUCCESS,
  CREATE_BLOCK_SUCCESS,
  UPDATE_BLOCK_SUCCESS,
  UPDATE_SLOT_SUCCESS,
  DESTROY_SLOT_SUCCESS,
  DESTROY_SLOT_FAILURE,
  FETCHING_APPOINTMENT,
  RECEIVE_APPOINTMENT,
};

const actions = {
  requestingScheduleBaseData(payload) {
    return getStore().dispatch({ type: REQUESTING_SCHEDULE_BASE_DATA, payload });
  },

  receiveScheduleBaseData(payload) {
    return getStore().dispatch({ type: RECEIVE_SCHEDULE_BASE_DATA, payload });
  },

  requestingAppointmentSlots(payload) {
    return getStore().dispatch({ type: REQUESTING_APPOINTMENT_SLOTS, payload });
  },
  receiveAppointmentSlots(payload) {
    return getStore().dispatch({ type: RECEIVE_APPOINTMENT_SLOTS, payload });
  },

  navigateDate(payload) {
    return getStore().dispatch({ type: NAVIGATE_DATE, payload });
  },

  updateView(payload) {
    return getStore().dispatch({ type: UPDATE_VIEW, payload });
  },

  selectEvent(payload) {
    return getStore().dispatch({ type: SELECT_EVENT, payload });
  },

  newSlotSelection(payload) {
    return getStore().dispatch({ type: NEW_SLOT_SELECTION, payload });
  },

  hideModal(payload) {
    return getStore().dispatch({ type: HIDE_MODAL, payload });
  },

  changeSelectedProviderEmployment(payload) {
    return getStore().dispatch({ type: CHANGE_SELECTED_PROVIDER_EMPLOYMENT, payload });
  },

  changeSelectedLocation(payload) {
    return getStore().dispatch({ type: CHANGE_SELECTED_LOCATION, payload });
  },

  createSlotSuccess(payload) {
    return getStore().dispatch({ type: CREATE_SLOT_SUCCESS, payload });
  },

  createBlockSuccess(payload) {
    return getStore().dispatch({ type: CREATE_BLOCK_SUCCESS, payload });
  },

  updateSlotSuccess(payload) {
    return getStore().dispatch({ type: UPDATE_SLOT_SUCCESS, payload });
  },

  destroySlotSuccess(payload) {
    return getStore().dispatch({ type: DESTROY_SLOT_SUCCESS, payload });
  },

  destroySlotFailure(payload) {
    return getStore().dispatch({ type: DESTROY_SLOT_FAILURE, payload });
  },

  fetchingAppointment(payload) {
    return getStore().dispatch({ type: FETCHING_APPOINTMENT, payload });
  },

  receiveAppointment(payload) {
    return getStore().dispatch({ type: RECEIVE_APPOINTMENT, payload });
  },

  changeFocusedProviderEmployment({ providerEmploymentId }) {
    actions.changeSelectedProviderEmployment({ providerEmploymentId });
    actions.fetchAppointmentSlots();
  },

  navigateToDate(newDate) {
    actions.navigateDate({ newDate: moment(newDate) }); // one of: 'month', 'week', 'day'
    actions.fetchAppointmentSlots();
  },

  navigateView(newView) {
    actions.updateView({ newView });
    actions.fetchAppointmentSlots();
  },

  fetchScheduleBaseData() {
    actions.requestingScheduleBaseData();
    return api.get('/agent_portal/api/schedule.json').then(({ data }) => {
      actions.receiveScheduleBaseData({
        providerEmployments: data.providerEmployments,
        now: data.now,
      });
    });
  },

  fetchAppointmentSlots() {
    const { selectedProviderEmploymentId, date } = getStore().getState().calendar;
    const requestId = uid();
    actions.requestingAppointmentSlots({ requestId });
    const timeRangeUnit = getStore().getState().calendar.view;
    const params = {
      provider_employment_id: selectedProviderEmploymentId,
      from: moment(date)
        .startOf(timeRangeUnit)
        .format('YYYY-MM-DDTHH:mm:ss'),
      to: moment(date)
        .endOf(timeRangeUnit)
        .format('YYYY-MM-DDTHH:mm:ss'),
    };
    return api.get('/agent_portal/api/appointment_slots.json', { params }).then(response => {
      const data = response.data;
      actions.receiveAppointmentSlots({
        appointmentSlots: data.data,
        requestId,
      });
    });
  },

  createSlot(data) {
    const { selectedProviderEmploymentId, providerEmployments } = getStore().getState().calendar;
    const providerId = providerEmployments.find(
      pe => pe.providerEmploymentId === selectedProviderEmploymentId
    ).providerId;
    const startMoment = moment(
      `${data.selectionStartDate} ${data.selectionStartTime}`,
      'MM/DD/YYYY HH:mm a'
    );
    const endMoment = moment(
      `${data.selectionEndDate} ${data.selectionEndTime}`,
      'MM/DD/YYYY HH:mm a'
    );
    const duration = moment.duration(endMoment.diff(startMoment)).asMinutes();
    const params = {
      provider_employment_id: selectedProviderEmploymentId,
      appointment_slot: {
        appointment_type_id: undefined,
        duration,
        start_time: startMoment.format('YYYY-MM-DDTHH:mm:ss'),
        end_time: endMoment.format('YYYY-MM-DDTHH:mm:ss'),
        high_priority_only: data.highPriorityOnly,
        label: 'New slot',
        permit_self_referrals: data.permitSelfReferral,
        location_id: data.locationId,
        provider_employment_id: selectedProviderEmploymentId,
        provider_id: providerId,
        type: 'AppointmentSlot',
        virtual: false,
      },
    };
    return api.post('/agent_portal/api/appointment_slots.json', params).then(response => {
      const { appointmentSlot } = response.data;
      return actions.createSlotSuccess({ appointmentSlot });
    });
  },

  createBlock(data) {
    return api.post('/agent_portal/api/blocks', data).then(response => {
      // alert('it worked');
      actions.createBlockSuccess();
    });
  },

  updateSlot(appointmentSlotId, formData) {
    const { selectedProviderEmploymentId, providerEmployments } = getStore().getState().calendar;
    const providerId = providerEmployments.find(
      pe => pe.providerEmploymentId === selectedProviderEmploymentId
    ).providerId;
    const startMoment = moment(
      `${formData.selectionStartDate} ${formData.selectionStartTime}`,
      'MM/DD/YYYY HH:mm a'
    );
    const endMoment = moment(
      `${formData.selectionEndDate} ${formData.selectionEndTime}`,
      'MM/DD/YYYY HH:mm a'
    );
    const duration = moment.duration(endMoment.diff(startMoment)).asMinutes();
    const params = {
      appointment_slot: {
        appointment_type_id: undefined,
        duration,
        start_time: startMoment.format('YYYY-MM-DDTHH:mm:ss'),
        end_time: endMoment.format('YYYY-MM-DDTHH:mm:ss'),
        high_priority_only: formData.highPriorityOnly,
        label: 'Updated event',
        permit_self_referrals: formData.permitSelfReferral,
        location_id: formData.locationId,
        provider_employment_id: selectedProviderEmploymentId,
        provider_id: providerId,
        type: 'AppointmentSlot',
        virtual: false,
      },
    };
    return api
      .put(`/agent_portal/api/appointment_slots/${appointmentSlotId}.json`, params)
      .then(response => {
        const { appointmentSlot } = response.data;
        actions.updateSlotSuccess({ appointmentSlot });
        FlashMessageActions.flashSuccess({ message: 'Slot updated!' });
      });
  },

  destroySlot(appointmentSlotId) {
    return api
      .delete(`/agent_portal/api/appointment_slots/${appointmentSlotId}.json`)
      .then(response => {
        if (response.data.success) {
          actions.destroySlotSuccess({ appointmentSlotId });
        } else if (response.data.errorCode === 'already_taken') {
          actions.destroySlotFailure({
            reason: 'already_taken',
            referralIdThatTookSlot: response.data.referralId,
          });
        }
      });
  },

  fetchAppointment(appointmentId) {
    actions.fetchingAppointment();
    return api.get(`/agent_portal/api/appointments/${appointmentId}.json`).then(response => {
      actions.receiveAppointment(response.data.appointment);
    });
  },
};

export default actions;
